import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Calendar, FileText, Edit, Trash2, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableOwnerSelect } from '@/components/SearchableOwnerSelect';

const StatusBadge = ({ status }) => {
  const statusColors = {
    'Scheduled': 'bg-blue-100 text-blue-800',
    'In Progress': 'bg-yellow-100 text-yellow-800',
    'Completed': 'bg-green-100 text-green-800',
    'Cancelled': 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

export const Visits = () => {
  const [visits, setVisits] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [owners, setOwners] = useState([]);
  const [pets, setPets] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [editingVisit, setEditingVisit] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingVisitId, setDeletingVisitId] = useState(null);
  const [formData, setFormData] = useState({
    owner_id: '',
    pet_id: '',
    veterinarian_id: '',
    visit_type_id: '',
    status: 'Scheduled',
    visit_date: '',
    notes: '',
  });

  useEffect(() => {
    fetchVisits();
    fetchVisitTypes();
    fetchOwners();
    fetchPets();
    fetchDoctors();
  }, []);

  const fetchVisits = async () => {
    try {
      const response = await api.get('/visits');
      setVisits(response.data);
    } catch (error) {
      toast.error('Failed to load visits');
    }
  };

  const fetchVisitTypes = async () => {
    try {
      const response = await api.get('/visit-types');
      setVisitTypes(response.data);
    } catch (error) {
      console.log('Visit types not yet set up');
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await api.get('/owners');
      setOwners(response.data);
    } catch (error) {
      toast.error('Failed to load owners');
    }
  };

  const fetchPets = async () => {
    try {
      const response = await api.get('/pets');
      setPets(response.data);
    } catch (error) {
      toast.error('Failed to load pets');
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to load doctors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.owner_id) {
      toast.error('Please select an owner');
      return;
    }
    if (!formData.pet_id) {
      toast.error('Please select a pet');
      return;
    }
    try {
      if (editingVisit) {
        await api.put(`/visits/${editingVisit.id}`, formData);
        toast.success('Visit updated successfully');
      } else {
        await api.post('/visits', formData);
        toast.success('Visit created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ owner_id: '', pet_id: '', veterinarian_id: '', visit_type_id: '', status: 'Scheduled', visit_date: '', notes: '' });
      setEditingVisit(null);
      fetchVisits();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((e) => e.msg).join(', ')
        : typeof detail === 'string'
        ? detail
        : 'Operation failed';
      toast.error(message);
    }
  };

  const handleEdit = (visit) => {
    setEditingVisit(visit);
    setFormData({
      owner_id: visit.owner_id,
      pet_id: visit.pet_id,
      veterinarian_id: visit.veterinarian_id || '',
      visit_type_id: visit.visit_type_id || '',
      status: visit.status,
      visit_date: visit.visit_date,
      notes: visit.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeletingVisitId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/visits/${deletingVisitId}`);
      toast.success('Visit deleted successfully');
      fetchVisits();
    } catch (error) {
      toast.error('Failed to delete visit');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingVisitId(null);
    }
  };

  const navigate = useNavigate();

  const handleViewDetails = (visit) => {
    navigate(`/dashboard/visits/${visit.id}`);
  };

  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || 'Unknown';
  const getPetName = (petId) => pets.find((p) => p.id === petId)?.name || 'Unknown';
  const getDoctorName = (doctorId) => doctors.find((d) => d.id === doctorId)?.full_name || 'Not assigned';
  const getVisitTypeName = (typeId) => visitTypes.find((t) => t.id === typeId)?.name || 'Unknown';

  const filteredPets = formData.owner_id ? pets.filter((pet) => pet.owner_id === formData.owner_id) : [];
  const [search, setSearch] = useState('');

  const filteredVisits = visits.filter((visit) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      getPetName(visit.pet_id).toLowerCase().includes(q) ||
      getOwnerName(visit.owner_id).toLowerCase().includes(q) ||
      getVisitTypeName(visit.visit_type_id).toLowerCase().includes(q) ||
      visit.status.toLowerCase().includes(q) ||
      (visit.notes || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 md:p-12" data-testid="visits-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Visits</h1>
          <p className="text-gray-600">Manage veterinary visits and medical records</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingVisit(null);
                setFormData({ owner_id: '', pet_id: '', veterinarian_id: '', visit_type_id: '', status: 'Scheduled', visit_date: '', notes: '' });
              }}
              data-testid="add-visit-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Visit
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingVisit ? 'Edit Visit' : 'Add New Visit'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="visit-form">
              <div>
                <Label htmlFor="owner_id">Owner</Label>
                <SearchableOwnerSelect
                  owners={owners}
                  value={formData.owner_id}
                  onValueChange={(value) => setFormData({ ...formData, owner_id: value, pet_id: '' })}
                  placeholder="Select owner"
                  testId="visit-owner-select"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="pet_id">Pet</Label>
                <Select value={formData.pet_id} onValueChange={(value) => setFormData({ ...formData, pet_id: value })} disabled={!formData.owner_id}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="visit-pet-select">
                    <SelectValue placeholder={formData.owner_id ? "Select pet" : "Select owner first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPets.map((pet) => (
                      <SelectItem key={pet.id} value={pet.id}>
                        {pet.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="visit_type_id">Visit Type</Label>
                <Select value={formData.visit_type_id} onValueChange={(value) => setFormData({ ...formData, visit_type_id: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="visit-type-select">
                    <SelectValue placeholder="Select visit type" />
                  </SelectTrigger>
                  <SelectContent>
                    {visitTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="veterinarian_id">Veterinarian (Optional)</Label>
                <Select value={formData.veterinarian_id} onValueChange={(value) => setFormData({ ...formData, veterinarian_id: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="visit-vet-select">
                    <SelectValue placeholder="Select veterinarian" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        {doctor.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="visit-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="visit_date">Visit Date & Time</Label>
                <Input
                  id="visit_date"
                  type="datetime-local"
                  value={formData.visit_date}
                  onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="visit-date-input"
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="rounded-xl mt-2"
                  placeholder="Add any relevant notes..."
                  data-testid="visit-notes-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="visit-submit-button">
                {editingVisit ? 'Update Visit' : 'Create Visit'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <TabsList className="grid w-full sm:max-w-md grid-cols-2">
            <TabsTrigger value="list" className="rounded-full" data-testid="list-view-tab">
              <FileText className="w-4 h-4 mr-2" />
              List View
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-full" data-testid="calendar-view-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar View
            </TabsTrigger>
          </TabsList>
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search visits..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="visit-search-input"
            />
          </div>
        </div>

        <TabsContent value="list">
          <div className="space-y-4">
            {filteredVisits.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-white rounded-3xl shadow-lg">
                {search ? 'No visits match your search.' : 'No visits found. Add your first visit to get started!'}
              </div>
            ) : (
              filteredVisits.map((visit, idx) => (
                <div
                  key={visit.id}
                  className="bg-white rounded-3xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 mb-2"
                  data-testid={`visit-item-${idx}`}
                >
        <div className="flex items-start flex-wrap gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-heading font-bold text-sage-800">{getVisitTypeName(visit.visit_type_id)}</h3>
                        <StatusBadge status={visit.status} />
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {getPetName(visit.pet_id)} - {getOwnerName(visit.owner_id)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(visit.visit_date).toLocaleString()} • Dr. {getDoctorName(visit.veterinarian_id)}
                      </p>
                      {visit.notes && <p className="text-sm text-gray-600 mt-2 italic">{visit.notes}</p>}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDetails(visit)}
                        className="rounded-full"
                        data-testid={`view-visit-${idx}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(visit)}
                        className="rounded-full"
                        data-testid={`edit-visit-${idx}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(visit.id)}
                        className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-visit-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="calendar">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="text-center text-gray-600">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-sage-400" />
              <p className="text-lg">Calendar view coming soon!</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {selectedVisit && (
        <VisitDetailsModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} owners={owners} pets={pets} doctors={doctors} visitTypes={visitTypes} />
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the visit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const VisitDetailsModal = ({ visit, onClose, owners, pets, doctors, visitTypes }) => {
  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || 'Unknown';
  const getPetName = (petId) => pets.find((p) => p.id === petId)?.name || 'Unknown';
  const getDoctorName = (doctorId) => doctors.find((d) => d.id === doctorId)?.full_name || 'Not assigned';
  const getVisitTypeName = (typeId) => visitTypes.find((t) => t.id === typeId)?.name || 'Unknown';

  return (
    <Dialog open={!!visit} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader>
          <DialogTitle>Visit Details - {getVisitTypeName(visit.visit_type_id)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-4">BASIC INFORMATION</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Pet</p>
                <p className="font-semibold text-gray-800">{getPetName(visit.pet_id)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Owner</p>
                <p className="font-semibold text-gray-800">{getOwnerName(visit.owner_id)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Veterinarian</p>
                <p className="font-semibold text-gray-800">
                  {visit.veterinarian_id && getDoctorName(visit.veterinarian_id)
                    ? `Dr. ${getDoctorName(visit.veterinarian_id)}`
                    : 'Not assigned'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Visit Type</p>
                <p className="font-semibold text-gray-800">{getVisitTypeName(visit.visit_type_id)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <StatusBadge status={visit.status} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Date & Time</p>
                <p className="font-semibold text-gray-800">{new Date(visit.visit_date).toLocaleString()}</p>
              </div>
            </div>
          </div>
          {visit.notes && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-2">NOTES</h3>
              <p className="text-gray-800">{visit.notes}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
